import { t } from '@exam-prep/i18n';
import { Button } from '@exam-prep/ui';
import { useState } from 'react';
import type { AttemptView } from '../../../lib/api-types';
import { Calculator } from '../../calculator/Calculator';
import type { Local, OpenQuestion } from '../model/palette';
import { QuestionFigure } from '../../../components/QuestionFigure';
import { ReportQuestion, type ReportControls } from './ReportQuestion';

export function QuestionPane({
  report,
  question: q,
  current: cur,
  ui,
  locked,
  transitioning,
  isFirst,
  isLast,
  onChoose,
  onMark,
  onClear,
  onPrev,
  onNext,
}: {
  report: ReportControls;
  question: OpenQuestion;
  current: Local;
  ui: AttemptView['paper']['ui'];
  locked: boolean;
  transitioning: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChoose: (i: number) => void;
  onMark: () => void;
  onClear: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const [tool, setTool] = useState<'calc' | null>(null);
  return (
    <>
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded-full border border-input px-2.5 py-0.5 font-display font-bold text-foreground">
          {t('miniapp.player.question', { n: q.n })}
        </span>
        {q.section}
        {q.source ? (
          <span className="ml-auto rounded bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground">
            {q.source}
          </span>
        ) : null}
      </p>
      <p className="text-base leading-relaxed whitespace-pre-wrap">{q.stem}</p>
      {q.figure ? <QuestionFigure figure={q.figure} /> : null}
      <div className="space-y-2">
        {q.options.map((o, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChoose(i)}
            aria-pressed={cur.choice === i}
            disabled={locked}
            className="tap-row text-sm whitespace-pre-wrap"
          >
            <span className="letter" aria-hidden>
              {String.fromCharCode(65 + i)}
            </span>
            <span className="sr-only">{String.fromCharCode(65 + i)}.</span>
            <span>{o}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {ui.markForReview ? (
          <Button variant="outline" size="sm" onClick={onMark}>
            {cur.marked ? t('miniapp.player.unmark') : t('miniapp.player.mark')}
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={onClear}>
          {t('miniapp.player.clear')}
        </Button>
        {ui.calculator ? (
          <Button
            variant={tool === 'calc' ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={tool === 'calc'}
            onClick={() => setTool(tool === 'calc' ? null : 'calc')}
          >
            {t('miniapp.player.calculator')}
          </Button>
        ) : null}
      </div>
      {tool === 'calc' ? <Calculator /> : null}
      <ReportQuestion key={q.itemId} itemId={q.itemId} report={report} />
      <div className="flex justify-between gap-3">
        <Button variant="secondary" disabled={transitioning || isFirst} onClick={onPrev}>
          {t('miniapp.player.prev')}
        </Button>
        <Button disabled={transitioning || isLast} onClick={onNext}>
          {t('miniapp.player.next')}
        </Button>
      </div>
    </>
  );
}
